


import { Component, Vue } from "vue-property-decorator";

import { AgentKb } from "App";
import { EventBus } from "App/IOC";
import { AgentKbUsage, AgentKbUsageRow } from "App/Models/AgentKb";

import { CompanyOf, LoadCompanies } from "./Companies";

/* REPONSES IA, L'USAGE ( Fred, 26/09/2026 ) — qui se sert de l'agent, et ce
   que ca consomme : par groupe, et par modele ( `usage?by=group`, `by=model` ).

   Les JETONS DE CACHE sont a part, et comptent : le sommaire des articles est
   relu en cache a chaque question ( 1,68 M lus contre 333 k en entree sur
   septembre, mesure du 26/09/2026 ). Un total sans eux sous-estime l'usage.

   Pas de montant en euros : le service ne rend que des jetons, et un tarif
   recopie ici vieillirait sans prevenir. La ligne sans cle ( `cle: null` ) est
   celle des echanges sans modele — refus, erreurs — et reste affichee telle
   quelle.

   ⚠️ `to` est EXCLUSIF : on envoie le lendemain du dernier jour. */

type Period = 7 | 30 | 90 | 365;

function isoDay( date: Date ): string {
    const month = String( date.getMonth() + 1 ).padStart( 2, "0" );
    const day = String( date.getDate() ).padStart( 2, "0" );

    return `${ date.getFullYear() }-${ month }-${ day }`;
}

@Component
export default class AgentKbUsageView extends Vue {
    private readonly periods: Period[] = [ 7, 30, 90, 365 ];

    private period: Period = 30;

    private byGroup: AgentKbUsage | null = null;
    private byModel: AgentKbUsage | null = null;
    private loading: boolean = false;

    private alive: boolean = true;

    private get groups(): AgentKbUsageRow[] {
        return this.byGroup !== null
            ? this.byGroup.items.slice().sort( ( a, b ) => b.echanges - a.echanges )
            : [];
    }

    private get models(): AgentKbUsageRow[] {
        return this.byModel !== null
            ? this.byModel.items.slice().sort( ( a, b ) => b.echanges - a.echanges )
            : [];
    }

    private created(): void {
        this.fetch();
    }

    private beforeDestroy(): void {
        this.alive = false;
    }

    private selectPeriod( period: Period ): void {
        this.period = period;
        this.fetch();
    }

    private async fetch(): Promise<void> {
        const from = new Date();
        const to = new Date();

        from.setDate( from.getDate() - this.period + 1 );
        to.setDate( to.getDate() + 1 );

        const range = { from: isoDay( from ), to: isoDay( to ) };

        this.loading = true;

        /* En file : en parallele, chaque appel eveille sa propre instance du
           service ( voir `Index.vue`, `refresh` ). */
        const [ groups, groupError ] = await AgentKb.Usage( "group", range );
        const [ models, modelError ] = await AgentKb.Usage( "model", range );

        if( !this.alive )
            return;

        this.loading = false;

        const error = groupError !== undefined ? groupError : modelError;

        if( error !== undefined )
            EventBus.Publish( "Error", error );

        this.byGroup = groups !== undefined ? groups : null;
        this.byModel = models !== undefined ? models : null;

        if( groups !== undefined )
            void LoadCompanies( groups.items.map( row => row.cle ) );
    }

    /* Un groupe mene a ses questions, dans la vue generale. */
    private openGroup( row: AgentKbUsageRow ): void {
        if( row.cle !== null )
            this.$router.push( { path: "/agent-ia", query: { group: row.cle } } );
    }

    private companyOf( group: string | null ): string {
        return CompanyOf( group );
    }

    private cached( row: AgentKbUsageRow ): number {
        return row.cacheReadTokens + row.cacheWriteTokens;
    }

    private total( row: AgentKbUsageRow ): number {
        return row.inputTokens + row.outputTokens + this.cached( row );
    }

    private perQuestion( row: AgentKbUsageRow ): string {
        if( row.echanges === 0 )
            return "—";

        return this.number( Math.round( this.total( row ) / row.echanges ) );
    }

    private duration( row: AgentKbUsageRow ): string {
        if( row.echanges === 0 || row.durationMsTotal === 0 )
            return "—";

        const seconds = row.durationMsTotal / row.echanges / 1000;

        return seconds.toLocaleString( "fr-FR", { maximumFractionDigits: 1 } ) + " s";
    }

    private number( value: number ): string {
        return value.toLocaleString( "fr-FR" );
    }

    private periodLabel( period: Period ): string {
        return this.$t( "AGENTKB_period" + period ) as string;
    }
}
